/**
 * The diverging bar model (plots plan P): the bar Python stamps, drawn.
 *
 * Moved unchanged from `slides/rating-likert-split.ts` (the non-legacy half):
 * band tones, the centre line, the pieces either side of it, and the band
 * wording. Both the plot path (`planDivergingLikertRow`) and the legacy
 * reader draw through it, so a stored deck and a new one paint one bar.
 */
import type { RatingLikertSlideData } from '../../slides/renderer-internal-types'
import type { RatingStatistics } from '@digital-twins/shared-types'

/* ------------------------------------------------------------------------- *
 * The bar the python builder stamps.
 *
 * `segments` arrives ordered low -> high with a `band` per segment, computed
 * against the unreversed policy thresholds and flipped once for a reversed
 * scale. Everything below DRAWS that list. Nothing here re-derives a band, a
 * bin or a threshold: a second derivation in TypeScript is exactly the
 * divergence the python builder exists to prevent.
 *
 * The legacy path (a payload with no `segments`) is folded into the same model
 * through `bandSegments`, so the slide, the drilldown figure and the generic
 * inner view all draw one bar with one caption rule instead of the three copies
 * they carried before.
 * ------------------------------------------------------------------------- */

export type RatingBand = 'favorable' | 'middle' | 'unfavorable' | 'mixed'

export interface RatingBandSegment {
  label: string
  /** The printed share, as Python rounded it. */
  value: number
  band: RatingBand
  /**
   * The unrounded share the segment is drawn with (the payload's `exactShare`),
   * so a bar's segments fill it however the printed figures round. A segment
   * read from a stored slide has none and draws `value`.
   */
  exactShare?: number
}

/**
 * How much of its own colour a band carries against the slide surface. The soft
 * tokens are a `color-mix(...)` of the strong colour into the page background,
 * which `parseColorToRgb` cannot read, so the mix is declared here and the
 * caption colour is decided from the mix rather than from the token.
 */
export const RATING_BAND_STRONG_FILL = 1
export const RATING_BAND_SOFT_FILL = 0.14
/** The middle band sits on the page's own neutral, so its caption reads as ink. */
export const RATING_BAND_NEUTRAL_FILL = 0

const BAND_FAVORABLE = 'var(--slide-diverging-positive)'
const BAND_FAVORABLE_SOFT = 'var(--slide-diverging-positive-soft)'
const BAND_UNFAVORABLE = 'var(--slide-diverging-negative)'
const BAND_UNFAVORABLE_SOFT = 'var(--slide-diverging-negative-soft)'
/**
 * One colour for the middle band. `--slide-diverging-neutral` does not exist in
 * this branch's ancestry, so this reuses the neutral the deck already draws
 * bands with (ContinuousDensitySlide's IQR band takes the same token).
 */
const BAND_MIDDLE = 'var(--slide-neutral-mid, #D7D7D7)'

export interface RatingBandTone {
  color: string
  fill: number
}

export interface RatingBarPiece extends RatingBandSegment {
  index: number
  /** The share this piece is drawn with: `exactShare`, else the printed `value`. */
  share: number
  /** Part of this segment's share drawn left of the centre line. */
  leftValue: number
  /** Part drawn right of it. A middle band is halved here and nowhere else. */
  rightValue: number
  tone: RatingBandTone
}

export interface RatingBarLegendEntry {
  band: RatingBand
  label: string
  /** A CSS background: a colour, or a two-stop gradient for a two-sided band. */
  color: string
  /** The scale-end words the stamped legend gives this entry; absent on a legacy read. */
  anchor?: string | null
}

/** One measured group of the bar: the Favorable / Neutral / Unfavorable read. */
export interface RatingBarGroup {
  band: RatingBand
  label: string
  value: number
}

export interface RatingBarPlan {
  pieces: Array<RatingBarPiece>
  leftTotal: number
  rightTotal: number
  /**
   * Favorable less unfavorable, read off `band` and never off which side of the
   * centre line a segment landed on. `mixed` straddles a policy threshold, so it
   * is claimed by neither pole. On every shape but the 0-100 bins (the only unit
   * with `mixed` segments) this is exactly `favorablePercent - unfavorablePercent`.
   */
  net: number
  legend: Array<RatingBarLegendEntry>
  /**
   * The three (or four) measured groups, summed from the very segments the bar
   * paints. Whatever prints a Favorable / Neutral / Unfavorable line reads this
   * rather than re-deriving groups from the histogram, which is how a 1-7 `5`
   * came to be counted as favorable in a line beside a bar drawing it as
   * middle.
   */
  groups: Array<RatingBarGroup>
}

export interface RatingBandLabels {
  favorable: string
  middle: string
  unfavorable: string
  mixed: string
}

function firstLabel(...candidates: Array<string | null | undefined>) {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }
  return ''
}

/**
 * What the bands are called. Measured statistics win: they carry the unit's own
 * wording ("Promoters", "4-5") and are already localized and already reversed.
 * `displayLabels` is the deck-label fallback for a payload without statistics.
 */
export function ratingBandLabels(
  statistics?: RatingStatistics | null,
  displayLabels?: RatingLikertSlideData['displayLabels'],
  /**
   * The unit's own wording, `[favorable, middle, unfavorable]`, as `splitRatingBuckets`
   * derives it from the scale kind. Reached only on a payload with no measured
   * statistics, where it is the one thing that knows an NPS bar says "Promoters" and not
   * "Favorable" -- `displayLabels` carries no NPS wording at all. On every other unit it
   * is the range each end covers ("6-10"): without measured statistics a midpoint split
   * is not favorable share. Those words used to reach
   * the slide through the three summary tiles, which read the same array; the tiles are
   * gone and the legend says it now.
   */
  unitLabels?: Array<string>,
): RatingBandLabels {
  const measured = statistics?.labels
  // legacy reader only: read by the stored-slide split (`legacy/`); a stamped plot names its bands.
  return {
    favorable: firstLabel(measured?.favorable, unitLabels?.[0], displayLabels?.favorable, 'Favorable'),
    middle: firstLabel(measured?.middle, unitLabels?.[1], displayLabels?.neutral, 'Neutral'),
    unfavorable: firstLabel(measured?.unfavorable, unitLabels?.[2], displayLabels?.unfavorable, 'Unfavorable'),
    mixed: firstLabel(displayLabels?.mixed, 'Mixed'),
  }
}

/** A payload's `segments`, or null when it carries none and the legacy bar applies. */
export function readRatingSegments(value: unknown): Array<RatingBandSegment> | null {
  if (!Array.isArray(value) || value.length === 0) return null
  const segments: Array<RatingBandSegment> = []
  for (const entry of value) {
    if (entry == null || typeof entry !== 'object') return null
    const candidate = entry as Partial<RatingBandSegment>
    const band = candidate.band
    if (typeof candidate.label !== 'string') return null
    if (typeof candidate.value !== 'number' || !Number.isFinite(candidate.value)) return null
    if (band !== 'favorable' && band !== 'middle' && band !== 'unfavorable' && band !== 'mixed') {
      return null
    }
    segments.push({ label: candidate.label, value: Math.max(0, candidate.value), band })
  }
  return segments
}

/**
 * The pole a `mixed` bin sits beside, read by walking AWAY from the centre until
 * a decided band appears. `mixed` is drawn in that pole's soft tone: it is the
 * bin that straddles the threshold, so it is honestly half in. Walking outward
 * rather than assuming "left is unfavorable" is what keeps a reversed scale
 * right, where the low end of the bar is the favorable one.
 */
function outwardPole(
  segments: Array<RatingBandSegment>,
  index: number,
  middleStart: number,
): RatingBand | null {
  const step = index < middleStart ? -1 : 1
  for (let cursor = index + step; cursor >= 0 && cursor < segments.length; cursor += step) {
    const band = segments[cursor].band
    if (band === 'favorable' || band === 'unfavorable') return band
  }
  return null
}

function toneForBand(band: RatingBand, pole: RatingBand | null): RatingBandTone {
  if (band === 'favorable') return { color: BAND_FAVORABLE, fill: RATING_BAND_STRONG_FILL }
  if (band === 'unfavorable') return { color: BAND_UNFAVORABLE, fill: RATING_BAND_STRONG_FILL }
  if (band === 'middle') return { color: BAND_MIDDLE, fill: RATING_BAND_NEUTRAL_FILL }
  if (pole === 'favorable') return { color: BAND_FAVORABLE_SOFT, fill: RATING_BAND_SOFT_FILL }
  if (pole === 'unfavorable') return { color: BAND_UNFAVORABLE_SOFT, fill: RATING_BAND_SOFT_FILL }
  return { color: BAND_MIDDLE, fill: RATING_BAND_NEUTRAL_FILL }
}

/**
 * Where the centre line falls, in value units from the low end of the bar.
 *
 * It is read from `band`, never from the midpoint of the scale: on a 1-7 rating
 * the policy middle is 3, 4 and 5, so the line sits in the middle of that mass
 * and a 5 is no longer drawn as a favorable answer the way a scale-midpoint
 * split drew it. The middle band is halved across the line, which is the same
 * device the old four-band bar used, except that the two halves are now one
 * segment of one colour that reports its share once.
 */
/** The share a segment is drawn with (legacy reader only: a stored slide's segment has no `exactShare`). */
function drawnShare(segment: RatingBandSegment): number {
  return Math.max(0, segment.exactShare ?? segment.value)
}

function centreValue(segments: Array<RatingBandSegment>) {
  const middleStart = segments.findIndex((segment) => segment.band === 'middle')
  if (middleStart >= 0) {
    let middleEnd = middleStart
    for (let index = segments.length - 1; index > middleStart; index -= 1) {
      if (segments[index].band === 'middle') {
        middleEnd = index
        break
      }
    }
    let before = 0
    let middle = 0
    segments.forEach((segment, index) => {
      if (index < middleStart) {
        before += drawnShare(segment)
      } else if (index <= middleEnd) {
        middle += drawnShare(segment)
      }
    })
    return { centre: before + middle / 2, middleStart }
  }

  // No middle band at all (binary yes/no). The line goes on the seam between the
  // two decided groups; a bar of one band alone divides its own mass.
  const total = segments.reduce((sum, segment) => sum + drawnShare(segment), 0)
  const boundary = segments.findIndex((segment) => segment.band !== segments[0].band)
  if (boundary < 0) return { centre: total / 2, middleStart: segments.length }
  let before = 0
  for (let index = 0; index < boundary; index += 1) {
    before += drawnShare(segments[index])
  }
  return { centre: before, middleStart: boundary }
}

/**
 * The numeric range a segment's own label states, or null when the label is not
 * a range at all.
 *
 * Point and bin labels ARE their range ("4", "81-100"); every other wording a
 * segment can carry is not ("Promoters (9-10)", "Yes", "between 30 and 70",
 * "Favorable"). Returning null for those is what keeps a band that cannot be
 * read back from the bar on the wording the statistics gave it.
 */
function segmentLabelBounds(label: string): [number, number] | null {
  const match = /^\s*(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?\s*$/.exec(label)
  if (!match) {
    return null
  }
  const low = Number.parseFloat(match[1])
  const high = match[2] === undefined ? low : Number.parseFloat(match[2])
  if (!Number.isFinite(low) || !Number.isFinite(high)) {
    return null
  }
  return [low, high]
}

/**
 * What a band's swatch actually covers, read off the segments painted in it.
 *
 * The legend used to take `ratingStatistics.labels`, which states the POLICY
 * thresholds. On every unit with one bucket per scale point those two agree, so
 * nothing moves there. On `rating_0_100` they do not: favorable is >= 70, but
 * `61-80` straddles that cut and is painted `mixed`, so the swatch the reader
 * sees beside "70-100" contains only `81-100`. A legend has one job, which is
 * to say what the colour beside it means, so it is derived from the bands it is
 * drawn from and falls back to the statistics wording whenever the segments
 * cannot state a range themselves.
 *
 * Every segment of the band counts, including one measuring 0%: a point that
 * nobody chose is still inside the band the swatch stands for, and dropping it
 * would make the legend flicker between "6-7" and "6" on the same scale.
 */
function bandRangeLabel(pieces: Array<RatingBarPiece>): string | null {
  if (!pieces.length) {
    return null
  }
  let low = Number.POSITIVE_INFINITY
  let high = Number.NEGATIVE_INFINITY
  for (const piece of pieces) {
    const bounds = segmentLabelBounds(piece.label)
    if (bounds === null) {
      return null
    }
    low = Math.min(low, bounds[0])
    high = Math.max(high, bounds[1])
  }
  return low === high ? String(low) : `${low}-${high}`
}

/**
 * What one band is called, wherever the bar names it: the legend row and the
 * measured-groups read take the same answer from here.
 *
 * `mixed` is the one band whose segments are not contiguous (on 0-100 it is the
 * bin below the favorable cut AND the bin above the unfavorable one), so it
 * keeps its own word rather than claiming a span it does not cover.
 */
function bandLabel(
  pieces: Array<RatingBarPiece>,
  band: RatingBand,
  labels: RatingBandLabels,
  measured?: RatingStatistics['labels'] | null,
): string {
  if (band === 'mixed') {
    return labels.mixed
  }
  const stated = labels[band]
  // Only a label the STATISTICS supplied is a claim about where the policy cuts
  // the scale, and only such a claim can disagree with the bins the bar paints.
  // `displayLabels` wording ("Favorable", "Neutral") names what the band MEANS
  // and says nothing about which points it covers, so it always stands.
  if (!measured || measured[band] !== stated) {
    return stated
  }
  // The measured wording stands too wherever the bar cannot state a range of its
  // own: an NPS bar labels its segments "Promoters (9-10)", a binary one "Yes",
  // and a fractional sample's bar IS the three measured bands already.
  return bandRangeLabel(pieces.filter((piece) => piece.band === band)) ?? stated
}

/** One legend row per band present, in the order the bar paints them. */
function buildLegend(
  pieces: Array<RatingBarPiece>,
  labels: RatingBandLabels,
  measured?: RatingStatistics['labels'] | null,
): Array<RatingBarLegendEntry> {
  const entries: Array<RatingBarLegendEntry> = []
  for (const piece of pieces) {
    if (piece.value <= 0) continue
    const existing = entries.find((entry) => entry.band === piece.band)
    if (!existing) {
      entries.push({
        band: piece.band,
        label: bandLabel(pieces, piece.band, labels, measured),
        color: piece.tone.color,
      })
      continue
    }
    // A `mixed` band exists on both sides of the 0-100 bar in two soft tones.
    // One row, one label, a swatch that shows both, rather than the duplicate
    // entries the geometry legend used to print.
    if (existing.color !== piece.tone.color && !existing.color.startsWith('linear-gradient')) {
      existing.color = `linear-gradient(90deg, ${existing.color} 50%, ${piece.tone.color} 50%)`
    }
  }
  return entries
}

/**
 * The measured groups, high band first, as the read has always printed them.
 *
 * A band with no segment at all is left out; a band whose segments all measure
 * 0% is kept, because "Favorable: 0%" is a measurement and an absent row is
 * not.
 */
function buildGroups(
  pieces: Array<RatingBarPiece>,
  labels: RatingBandLabels,
  measured?: RatingStatistics['labels'] | null,
): Array<RatingBarGroup> {
  const order: Array<RatingBand> = ['favorable', 'middle', 'unfavorable', 'mixed']
  const groups: Array<RatingBarGroup> = []
  for (const band of order) {
    const banded = pieces.filter((piece) => piece.band === band)
    if (!banded.length) {
      continue
    }
    groups.push({
      band,
      label: bandLabel(pieces, band, labels, measured),
      value: banded.reduce((sum, piece) => sum + piece.value, 0),
    })
  }
  return groups
}

export function planRatingBar(
  segments: Array<RatingBandSegment>,
  labels: RatingBandLabels,
  /**
   * `ratingStatistics.labels`, when the payload carries them. Passed so the bar
   * can tell a threshold claim it may correct from deck wording it may not.
   */
  measured?: RatingStatistics['labels'] | null,
): RatingBarPlan | null {
  if (!segments.length) return null
  const { centre, middleStart } = centreValue(segments)
  const pieces: Array<RatingBarPiece> = []
  let cursor = 0
  let leftTotal = 0
  let rightTotal = 0
  let net = 0

  segments.forEach((segment, index) => {
    const value = Math.max(0, segment.value)
    const share = drawnShare(segment)
    const leftValue = Math.min(share, Math.max(0, centre - cursor))
    const rightValue = share - leftValue
    cursor += share
    leftTotal += leftValue
    rightTotal += rightValue
    if (segment.band === 'favorable') net += value
    if (segment.band === 'unfavorable') net -= value
    pieces.push({
      ...segment,
      value,
      share,
      index,
      leftValue,
      rightValue,
      tone: toneForBand(segment.band, outwardPole(segments, index, middleStart)),
    })
  })

  return {
    pieces, leftTotal, rightTotal, net,
    legend: buildLegend(pieces, labels, measured),
    groups: buildGroups(pieces, labels, measured),
  }
}

